import { type PROGRAMME_CHOICE_DEPARTMENT } from '../../../../Application/graphql/ApplicationForm/programmeChoice';
import { type FileData } from '../../../../../components';
import type { FUNDING_TAGS, MappingType } from '../../constant';
import { PAGE_TEXT } from '../../lang';
import { type FUNDING_STATUS } from '../../../../ScholarshipManagement';

export type Period = {
  from: Date | '';
  to: Date | '';
};

export enum FundingPanel {
  SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY = 'SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY',
  HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES = 'HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES',
  MIXED = 'MIXED',
}

export enum FundingSource {
  HONGKONG_PHD_FELLOWSHIP_SCHEME = 'HONGKONG_PHD_FELLOWSHIP_SCHEME',
  POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME = 'POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME',
  POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME = 'POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME',
  COMPETITIVE_3_PLUS_1_MATCHING_FUND_AND_PPPFS = 'COMPETITIVE_3_PLUS_1_MATCHING_FUND_AND_PPPFS',
  INCENTIVE_GRF_ECS_3_PLUS_1_MATCHING_FUND = 'INCENTIVE_GRF_ECS_3_PLUS_1_MATCHING_FUND',
  STRATEGIC_HIRING_SCHEME = 'STRATEGIC_HIRING_SCHEME',
  GLOBAL_STEM_PROFESSORSHIP_SCHEME = 'GLOBAL_STEM_PROFESSORSHIP_SCHEME',
  INCENTIVE_HKPFS = 'INCENTIVE_HKPFS',
  INCENTIVE_DUAL_PHD_DEGREE_PROGRAMME = 'INCENTIVE_DUAL_PHD_DEGREE_PROGRAMME',
  NON_STEM_NON_SHS_NEW_RECRUITS = 'NON_STEM_NON_SHS_NEW_RECRUITS',
  RESEARCH_UNIT = 'RESEARCH_UNIT',
  ALLOCAION_PHD_FOR_MAJOR_RENOWNED_FUNDING_AND_RIO = 'ALLOCAION_PHD_FOR_MAJOR_RENOWNED_FUNDING_AND_RIO',
  COLLABORATIVE_PHD_TRAINING_PROGRAMME = 'COLLABORATIVE_PHD_TRAINING_PROGRAMME',
  DUAL_PHD_DEGREE_PROGRAMME = 'DUAL_PHD_DEGREE_PROGRAMME',
  EXTERNAL_GRANTS_FUNDS = 'EXTERNAL_GRANTS_FUNDS',
  INTERNAL_GRANTS_FUNDS = 'INTERNAL_GRANTS_FUNDS',
  DEPARTMENTAL_EARNINGS_ACCOUNT = 'DEPARTMENTAL_EARNINGS_ACCOUNT',
  SELF_FINANCED = 'SELF_FINANCED',
  INTERNATIONAL_PHD_FELLOWSHIP_SCHEME = 'INTERNATIONAL_PHD_FELLOWSHIP_SCHEME',
}

export enum FundingRound {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
}

export enum ProjectDeclarationType {
  GRF_ECS_PROJECT_FUNDED_AFTER_AND_NOT_RECEIVED_SUPPORT_BEFORE = 'GRF_ECS_PROJECT_FUNDED_AFTER_AND_NOT_RECEIVED_SUPPORT_BEFORE',
  CHIEF_SUPERVISOR_NOT_GRF_ECS_PTOJECT_PI = 'CHIEF_SUPERVISOR_NOT_GRF_ECS_PTOJECT_PI',
  SUFFICIENT_FUNDING_FROM_SHS_STEM_PROJECT = 'SUFFICIENT_FUNDING_FROM_SHS_STEM_PROJECT',
}

export enum ProjectRole {
  SPECIAL_PI = 'SPECIAL_PI', // YES
  NON_SPECIAL_PI = 'NON_SPECIAL_PI', // NO
  CI = 'CI',
  PI = 'PI',
}

export type ProjectDeclaration = {
  content: ProjectDeclarationType;
  confirmed: boolean;
  accountCode?: string;
};

export type ProjectInformation = {
  role: ProjectRole | string | null;
  PIName: string;
  PIDepartment: PROGRAMME_CHOICE_DEPARTMENT | string | null;
  justification: string;
};

export type FundingPlanFormValue = {
  fundingPlan: FundingPlan[];
};

export type FundingPlan = {
  accountCode: string;
  period: Period;
};
export enum BonusType {
  MASTER_MPHIL_DEGREE = 'MASTER_MPHIL_DEGREE',
  WORK_EXPERIENCE = 'WORK_EXPERIENCE',
  COMMUNITY_SERVICE = 'COMMUNITY_SERVICE',
  EXHIBITIONS_AND_PERFORMANCES = 'EXHIBITIONS_AND_PERFORMANCES',
  BEST_PAPER = 'BEST_PAPER',
}

export type Bonus = {
  name: BonusType;
  justification: string;
  supportingDocuments: FileData[];
};

export type FundingSourceConfiguration = {
  mapping: FundingMappings;
  validTags: FUNDING_TAGS[];
};

export type FundingMappings = {
  COMPETITIVE: ConfigurationMapping[];
  NON_COMPETITIVE: ConfigurationMapping[];
  OTHER: ConfigurationMapping[];
};

export type FundingPeriod = {
  from: string;
  to: string;
};
export type ConfigurationMapping = {
  value: string;
  label: string;
  abbv: string;
  tags: FUNDING_TAGS[];
  disabled?: boolean;
  disabledDesc?: string;
  openingPeriod?: FundingPeriod[];
  recentOpeningPeriod?: FundingPeriod;
};

export type FundingsValue = {
  schema: MappingType | '';
  source: FundingSource | '';
  sufficientFundingConfirm: boolean;
  fundingPlan: FundingPlan[];
  requiredDocuments: FileData[];
  projectInformation: ProjectInformation;
  proofOfScheme?: FileData[];
  declaration: FundingDeclaration[];
  status?: FUNDING_STATUS;
  formFinished?: boolean;
  supportYears?: number;

  // for making up account code after panel
  stipendBudgetCreationConfirm: boolean;
  externalGrantsConfirm: boolean;
  accountInfoAccurateConfirm: boolean;
};

export type FundingDeclaration = {
  declaration: string;
  confirmed: boolean;
};

export type FundingPreSelectValue = {
  schema: MappingType | '';
  sources: FundingSource[];
  subjectClassification?: string;
  competitiveStudentship?: number | null | '';
};

/**
 *
 * declaration tailor-made for special fundings:
 * `PolyU Competitive PhD Funding`
 * `Global STEM Professorship Scheme (STEM)`
 * `Incentive GRF/ECS 3+1 Matching Fund`
 * `Global STEM Professorship Scheme (STEM)`
 * `Dual PhD Degree Programmes (University Level)`
 */
const FUNDING_TEXT = PAGE_TEXT.FUNDING_SOURCE;

export const fundingDeclarationMappings: Partial<
  Record<FundingSource, string>
> = {
  [FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME]:
    FUNDING_TEXT.POLYU_COMPETITIVE_CONFIRM,
  [FundingSource.DUAL_PHD_DEGREE_PROGRAMME]:
    FUNDING_TEXT.POLYU_COMPETITIVE_CONFIRM,
  [FundingSource.GLOBAL_STEM_PROFESSORSHIP_SCHEME]:
    FUNDING_TEXT.SHS_ROLE.CONFIRMATION_1,
  [FundingSource.INCENTIVE_GRF_ECS_3_PLUS_1_MATCHING_FUND]:
    FUNDING_TEXT.ROLE.CI.CONFIRMATION,
  [FundingSource.STRATEGIC_HIRING_SCHEME]: FUNDING_TEXT.SHS_ROLE.CONFIRMATION_1,
};

export const getFundingBasedDeclaration = (
  fundingSource: keyof typeof fundingDeclarationMappings,
): FundingDeclaration => {
  return {
    declaration: fundingDeclarationMappings[fundingSource] ?? '',
    confirmed: false,
  };
};
